import { filter, find, first, forEach, last, map, pipe } from 'remeda'
import type { Cleanup } from '../helpers/cleanup'
import type { ViewHook } from '../helpers/utils'
import { setupPageComponent } from '../helpers/webawesome-components'

type TabsShowDetail = Readonly<{
  panel: string
}>

type TabShowDetail = Readonly<{
  name: string
}>

type TabGroupElement = HTMLElement & {
  active: string
  updateComplete?: Promise<unknown>
}

type LinkedTab = Readonly<{
  link: HTMLAnchorElement
  tab: HTMLElement
}>

type TabsHookContext = ViewHook & {
  _tabsCleanup?: Cleanup
}

const isTabGroup = (element: HTMLElement): element is TabGroupElement =>
  element.tagName === 'WA-TAB-GROUP'

const isTab = (element: Element): element is HTMLElement =>
  element instanceof HTMLElement && element.tagName === 'WA-TAB'

const isTabLink = (element: Element): element is HTMLAnchorElement =>
  element instanceof HTMLAnchorElement &&
  element.hasAttribute('href') &&
  element.getAttribute('data-part') === 'wrapper'

const isLinkedTab = (value: LinkedTab | undefined): value is LinkedTab =>
  value !== undefined

const directTabs = (tabGroup: TabGroupElement): readonly HTMLElement[] =>
  pipe(Array.from(tabGroup.children), filter(isTab))

const linkedTab = (tab: HTMLElement): LinkedTab | undefined => {
  const link = pipe(Array.from(tab.children), find(isTabLink))

  return link ? { link, tab } : undefined
}

const linkedTabs = (tabGroup: TabGroupElement): readonly LinkedTab[] =>
  pipe(directTabs(tabGroup), map(linkedTab), filter(isLinkedTab))

const setScrollBoundary = (
  tabGroup: TabGroupElement,
  boundary: 'start' | 'end',
  available: boolean
): void => {
  const attribute = `data-scroll-${boundary}`
  const value = String(available)

  if (tabGroup.getAttribute(attribute) !== value) {
    tabGroup.setAttribute(attribute, value)
  }
}

type ScrollBoundary = 'start' | 'end'

const isRtl = (element: HTMLElement): boolean =>
  window.getComputedStyle(element).direction === 'rtl'

const isBoundaryEdgeVisible = (
  boundary: ScrollBoundary,
  item: DOMRectReadOnly,
  visible: DOMRectReadOnly,
  rtl: boolean
): boolean => {
  if (visible.width <= 0) {
    return false
  }

  const tolerance = 1

  if (boundary === 'start') {
    return rtl
      ? visible.right >= item.right - tolerance
      : visible.left <= item.left + tolerance
  }

  return rtl
    ? visible.left <= item.left + tolerance
    : visible.right >= item.right - tolerance
}

const isBoundaryVisibleInViewport = (
  boundary: ScrollBoundary,
  item: DOMRectReadOnly,
  viewport: DOMRectReadOnly,
  rtl: boolean
): boolean => {
  const intersects = item.right > viewport.left && item.left < viewport.right

  if (!intersects) {
    return false
  }

  const tolerance = 1

  if (boundary === 'start') {
    return rtl
      ? item.right <= viewport.right + tolerance
      : item.left >= viewport.left - tolerance
  }

  return rtl
    ? item.left >= viewport.left - tolerance
    : item.right <= viewport.right + tolerance
}

const setupScrollTracking = (
  tabGroup: TabGroupElement,
  startTab: HTMLElement,
  endTab: HTMLElement
): (() => void) => {
  const controller = new AbortController()
  const handleScroll: EventListener = (event: Event) => {
    if (!(event.target instanceof HTMLElement)) {
      return
    }

    const viewport = event.target.getBoundingClientRect()
    const startRect = startTab.getBoundingClientRect()
    const endRect = endTab.getBoundingClientRect()
    const rtl = isRtl(tabGroup)
    const startAvailable = !isBoundaryVisibleInViewport(
      'start',
      startRect,
      viewport,
      rtl
    )
    const endAvailable = !isBoundaryVisibleInViewport(
      'end',
      endRect,
      viewport,
      rtl
    )

    setScrollBoundary(tabGroup, 'start', startAvailable)
    setScrollBoundary(tabGroup, 'end', endAvailable)
  }

  const attachAfterRender = async (): Promise<void> => {
    await tabGroup.updateComplete

    if (controller.signal.aborted) {
      return
    }

    const nav = tabGroup.shadowRoot?.querySelector('[part~="nav"]')

    if (nav instanceof HTMLElement) {
      // Web Awesome exposes edge controls but not edge state; the documented
      // nav part is the stable boundary for captured scroller events.
      nav.addEventListener('scroll', handleScroll, {
        capture: true,
        signal: controller.signal
      })
    }
  }

  attachAfterRender().catch(() => undefined)

  return () => controller.abort()
}

const setupScrollBoundaries = (tabGroup: TabGroupElement): (() => void) => {
  const tabs = directTabs(tabGroup)
  const startTab = first(tabs)
  const endTab = last(tabs)

  if (
    startTab === undefined ||
    endTab === undefined ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return () => undefined
  }

  let active = true
  // Keep the last measurement during patches until the new observer reports.
  if (!tabGroup.hasAttribute('data-scroll-start')) {
    setScrollBoundary(tabGroup, 'start', false)
  }
  if (!tabGroup.hasAttribute('data-scroll-end')) {
    setScrollBoundary(tabGroup, 'end', false)
  }
  const cleanupScrollTracking = setupScrollTracking(tabGroup, startTab, endTab)

  const observer = new IntersectionObserver(
    (entries) => {
      if (!active) {
        return
      }

      const rtl = isRtl(tabGroup)

      pipe(
        entries,
        forEach((entry) => {
          if (entry.target === startTab) {
            setScrollBoundary(
              tabGroup,
              'start',
              !(
                entry.isIntersecting &&
                isBoundaryEdgeVisible(
                  'start',
                  entry.boundingClientRect,
                  entry.intersectionRect,
                  rtl
                )
              )
            )
          }

          if (entry.target === endTab) {
            setScrollBoundary(
              tabGroup,
              'end',
              !(
                entry.isIntersecting &&
                isBoundaryEdgeVisible(
                  'end',
                  entry.boundingClientRect,
                  entry.intersectionRect,
                  rtl
                )
              )
            )
          }
        })
      )
    },
    { root: tabGroup, threshold: [0, 0.99, 1] }
  )

  observer.observe(startTab)

  if (endTab !== startTab) {
    observer.observe(endTab)
  }

  return () => {
    active = false
    observer.disconnect()
    cleanupScrollTracking()
  }
}

const isActivationKey = (value: unknown): value is 'Enter' | ' ' =>
  value === 'Enter' || value === ' '

const eventKey = (event: Event): unknown =>
  'key' in event ? event.key : undefined

const eventTargetsLink = (event: Event, link: HTMLAnchorElement): boolean =>
  event.target instanceof Node && link.contains(event.target)

const setupLinkedTab = ({ link, tab }: LinkedTab): (() => void) => {
  const handleClick: EventListener = (event: Event) => {
    if (tab.hasAttribute('disabled')) {
      event.preventDefault()
      event.stopPropagation()
    }
  }
  const handleKeyDown: EventListener = (event: Event) => {
    if (!isActivationKey(eventKey(event))) {
      return
    }

    const disabled = tab.hasAttribute('disabled')

    if (eventTargetsLink(event, link)) {
      event.stopPropagation()

      if (disabled) {
        event.preventDefault()
      }

      return
    }

    if (event.target !== tab) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (!disabled) {
      link.click()
    }
  }

  link.addEventListener('click', handleClick)
  tab.addEventListener('keydown', handleKeyDown)

  return () => {
    link.removeEventListener('click', handleClick)
    tab.removeEventListener('keydown', handleKeyDown)
  }
}

const isTabsShowDetail = (value: unknown): value is TabsShowDetail =>
  typeof value === 'object' &&
  value !== null &&
  'panel' in value &&
  typeof value.panel === 'string'

const isTabShowDetail = (value: unknown): value is TabShowDetail =>
  typeof value === 'object' &&
  value !== null &&
  'name' in value &&
  typeof value.name === 'string'

const eventDetail = (event: Event): unknown =>
  'detail' in event ? event.detail : undefined

// Attaches tab commands, navigation, and overflow tracking to an upgraded group.
const setupTabs = (hook: TabsHookContext): Cleanup => {
  if (!isTabGroup(hook.el)) {
    return () => undefined
  }

  const tabGroup = hook.el
  const linkedTabCleanups = pipe(linkedTabs(tabGroup), map(setupLinkedTab))
  const cleanupScrollBoundaries = setupScrollBoundaries(tabGroup)
  const handleShow: EventListener = (event: Event) => {
    const detail = eventDetail(event)

    if (event.target === tabGroup && isTabsShowDetail(detail)) {
      tabGroup.active = detail.panel
    }
  }

  window.addEventListener('tabs:show', handleShow)

  const handleTabSwitch: EventListener | undefined = tabGroup.dataset
    .eventOnSwitch
    ? (event: Event) => {
        const detail = eventDetail(event)
        const eventName = tabGroup.dataset.eventOnSwitch

        if (
          event.target !== tabGroup ||
          eventName === undefined ||
          !isTabShowDetail(detail)
        ) {
          return
        }

        const payload = { panel: detail.name }
        const target = tabGroup.getAttribute('phx-target')

        if (target) {
          hook.pushEventTo(target, eventName, payload)
        } else {
          hook.pushEvent(eventName, payload)
        }
      }
    : undefined

  if (handleTabSwitch) {
    tabGroup.addEventListener('wa-tab-show', handleTabSwitch)
  }

  return () => {
    window.removeEventListener('tabs:show', handleShow)

    if (handleTabSwitch) {
      tabGroup.removeEventListener('wa-tab-show', handleTabSwitch)
    }

    pipe(
      linkedTabCleanups,
      forEach((cleanup) => cleanup())
    )
    cleanupScrollBoundaries()
  }
}

// Replaces current or pending setup using the latest rendered tab configuration.
const connectTabs = (hook: TabsHookContext): void => {
  hook._tabsCleanup?.()
  hook._tabsCleanup = setupPageComponent(hook.el, () => setupTabs(hook))
}

export const FacadeTabs = {
  // Waits for the group and its tabs before connecting browser behavior.
  mounted(this: TabsHookContext): void {
    connectTabs(this)
  },

  // Reconciles changed tabs and invalidates an older pending setup.
  updated(this: TabsHookContext): void {
    connectTabs(this)
  },

  // Releases current behavior and prevents a late import from reconnecting it.
  destroyed(this: TabsHookContext): void {
    this._tabsCleanup?.()
    this._tabsCleanup = undefined
    this.el.removeAttribute('data-scroll-start')
    this.el.removeAttribute('data-scroll-end')
  }
}
